/**
 * Contact Us Page
 * Route: /contact
 * Features: Hero section, contact form, info cards (email, phone, address, office hours)
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import {
  ArrowLeft,
  Clock,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  Headphones,
} from 'lucide-react';

/* ─── Subject options ─── */
const subjectOptions = [
  'General Inquiry',
  'Account & Login Issues',
  'Report a Listing',
  'Billing & Payments',
  'Safety Concern',
  'Bug Report',
  'Feature Request',
  'Business & Partnerships',
  'Other',
];

/* ─── Contact Info Card ─── */
interface InfoCardProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  accent: string;
}

const InfoCard: React.FC<InfoCardProps> = ({ icon, title, children, accent }) => (
  <div className="contact-info-card" id={`contact-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="contact-info-card__icon" style={{ background: accent, color: '#fff' }}>
      {icon}
    </div>
    <div className="contact-info-card__body">
      <h3 className="contact-info-card__title">{title}</h3>
      {children}
    </div>
  </div>
);

/* ─── Contact Page ─── */
const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    // Simulate send
    setTimeout(() => {
      setSending(false);
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 1200);
  };

  return (
    <div className="contact-page" id="contact-page">
      <SEO
        title="Contact Us"
        description="Have questions or feedback? Get in touch with the MemAds support team. We're here to help you buy and sell safely."
        canonical="/contact"
      />
      {/* ── Hero Header ── */}
      <section className="contact-hero">
        <div className="contact-hero__pattern" />
        <div className="contact-hero__inner">
          <Link to="/" className="contact-hero__back">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <h1 className="contact-hero__title">Get in Touch</h1>
          <p className="contact-hero__subtitle">
            Have a question or feedback? We'd love to hear from you. Our team typically responds within 24 hours.
          </p>
        </div>
      </section>

      {/* ── Main Content: Form + Info Cards ── */}
      <div className="contact-content">
        {/* Left: Contact Form */}
        <div className="contact-form-wrap" id="contact-form">
          <h2 className="contact-form-wrap__title">
            <MessageCircle className="h-5 w-5" />
            Send us a Message
          </h2>
          <p className="contact-form-wrap__desc">
            Fill out the form and our team will reach out shortly.
          </p>

          {submitted ? (
            <div className="contact-form__success" id="contact-success">
              <div className="contact-form__success-icon">
                <Send className="h-6 w-6" />
              </div>
              <h3 className="contact-form__success-title">Message Sent!</h3>
              <p className="contact-form__success-desc">
                Thank you for reaching out. Our team will get back to you within 24 hours.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="contact-form__success-btn"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="contact-form">
              {/* Name + Email Row */}
              <div className="contact-form__row">
                <div className="contact-form__field">
                  <label htmlFor="contact-name" className="contact-form__label">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    placeholder="Enter Full Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="contact-form__input"
                  />
                </div>
                <div className="contact-form__field">
                  <label htmlFor="contact-email" className="contact-form__label">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="contact-form__input"
                  />
                </div>
              </div>

              {/* Subject */}
              <div className="contact-form__field">
                <label htmlFor="contact-subject" className="contact-form__label">
                  Subject
                </label>
                <select
                  id="contact-subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="contact-form__select"
                >
                  <option value="" disabled>
                    Select a subject...
                  </option>
                  {subjectOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div className="contact-form__field">
                <label htmlFor="contact-message" className="contact-form__label">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  placeholder="How can we help you?"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="contact-form__textarea"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={sending}
                className="contact-form__submit"
                id="contact-submit"
              >
                {sending ? (
                  <>
                    <span className="spinner h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Right: Info Cards */}
        <aside className="contact-sidebar" id="contact-sidebar">
          <InfoCard
            icon={<Mail className="h-5 w-5" />}
            title="Email Us"
            accent="linear-gradient(135deg, #E8A317, #F5B731)"
          >
            <a href="mailto:support@memads.in" className="contact-info-card__link">
              support@memads.in
            </a>
            <p className="contact-info-card__sub">For general queries and support</p>
          </InfoCard>

          <InfoCard
            icon={<Phone className="h-5 w-5" />}
            title="WhatsApp Message Only"
            accent="linear-gradient(135deg, #16A34A, #22C55E)"
          >
            <a href="https://wa.me/918331914075" target="_blank" rel="noopener noreferrer" className="contact-info-card__link">
              +91 8331914075
            </a>
            <p className="contact-info-card__sub">Mon–Fri, 9 AM – 6 PM IST</p>
          </InfoCard>

          <InfoCard
            icon={<MapPin className="h-5 w-5" />}
            title="Visit Us"
            accent="linear-gradient(135deg, #3B82F6, #60A5FA)"
          >
            <p className="contact-info-card__link" style={{ cursor: 'default' }}>
              MemAds HQ,
            </p>
            <p className="contact-info-card__sub">
              Hi-Tech City, Hyderabad, Telangana State, India, Pincode:500081
            </p>
          </InfoCard>

          <InfoCard
            icon={<Clock className="h-5 w-5" />}
            title="Office Hours"
            accent="linear-gradient(135deg, #7C3AED, #A78BFA)"
          >
            <div className="contact-hours">
              <div className="contact-hours__row">
                <span>Monday – Friday</span>
                <span className="contact-hours__time">9:00 AM – 6:00 PM</span>
              </div>
              <div className="contact-hours__row">
                <span>Saturday</span>
                <span className="contact-hours__time">10:00 AM – 2:00 PM</span>
              </div>
              <div className="contact-hours__row">
                <span>Sunday</span>
                <span className="contact-hours__time contact-hours__closed">Closed</span>
              </div>
            </div>
          </InfoCard>
        </aside>
      </div>
    </div>
  );
};

export default ContactPage;
